import { Annotation, BBoxPixels, HiddenReason, SegmentationData } from "@/types/annotations";
import type { MeasurementDescriptor } from "@shared/types/measurementDescriptors";
import { buildSeedKeypointsForBox } from "@shared/types/measurementKeypointPlacement";
import type { ContainmentParams } from "@shared/containmentOverlapFilter";
import { decodeCompressedRLEToMask } from "@/utils/rleUtils";
import { CornerDownRight, Expand, MousePointer2, SquareDashedMousePointer, Trash2 } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Line, Rect } from "react-konva";
import { AnnotationViewerBase } from "../AnnotationViewerBase";
import { AnnotationSidebar } from "../AnnotationSidebar";
import { generateManualAnnotationId } from "../manualAnnotationIds";
import { AnnotationType, Point } from "../types";
import { SegmentationViewer, type MeasurementDragDraft } from "./SegmentationViewer";
import { DepthDebugView } from "./DepthDebugView";
import {
  applyBoxChangeToKeypoints,
  applyGeometryOnlyKeypointEdit,
  applyMeasurementKeypointEdit,
  measureNewBoxAxes,
  type BoxKeypointUpdate,
  type MeasurementKeypointName,
} from "./measurementEdit";
import { deriveMeasurementEditingMode, type DepthAvailability } from "@/lib/measurement/depthAvailability";
import { measureDistanceCm, type DepthFrame } from "@/lib/measurement/depth3d";
import { describeDepthAvailability } from "@/components/annotatedImageViewerLabels";
import { useToast } from "@/hooks/use-toast";
import { MEASUREMENT_HUD_CORNER_CLASSES, selectMeasurementHudCorner } from "./measurementDragHud";
import {
  applyComputedFilterVisibility,
  BBoxCandidate,
  computeMeasurementLengthPixelDistances,
  computeFilterHiddenReasonsForList,
  findToggleTargetIndex,
  getBlockingFilterHiddenReasons,
  getSegmentationDisplayBoundaryPixels,
  isDraftBoxDrawable,
  isAnnotationEffectivelyVisible,
  isManualBBoxAnnotation,
  orderCandidatesForReview,
  resolveSegmentationAnnotationColors,
  resolveEffectiveFilterThresholds,
  resolveDoubleClickTargetIndex,
  SegmentationFilterThresholds,
} from "./helpers";
import type { StageFocusRequest } from "../AnnotationViewerBase/helpers";
import type { ReviewLandingEdge, ReviewStepResult } from "@/utils/reviewObjectNavigation";
import { annotationHasMeasurementText, buildMeasurementEndpointLegend } from "@/utils/annotationMeasurementUtils";
import { MASK_COLORS } from "@/utils/annotationMaskColors";
import { shouldIgnoreSequenceNavigationKeys } from "@/utils/plotImageSequence";
import { useDepthDebugViewport } from "./useDepthDebugViewport";
import type { SegmentationFilterId, SegmentationFilterSettings } from "../annotationViewSettings";
import { resolveFilterSliderMax } from "../annotationViewSettings";
import {
  buildAnnotationClassRegistry,
  getAnnotationClassColor,
  normalizeAnnotationClassName,
} from "@shared/annotationClassColors";
import { AnnotationClassControls, NewBoxClassControl } from "../AnnotationClassControls";
import {
  canDrawAnnotationClass,
  countAnnotationsByClass,
  countVisibleAnnotationsBeforeClassFilter,
  filterAnnotationsForClassView,
  formatAnnotationObjectCount,
  getAnnotationClassShortcut,
  relabelBBoxAnnotation,
  resolveActiveAnnotationClass,
  shouldHandleAnnotationClassShortcut,
} from "../annotationClassInteractions";
import {
  demoteOutsideBoundaryAnnotations,
  getOutsideBoundaryCandidateState,
  isAcceptedOutsideBoundaryAnnotation,
  isAnnotationEligibleForOutsideBoundaryReveal,
  isBBoxOutsideMeasureBoundary,
  OUTSIDE_BOUNDARY_CANDIDATE_COLOR,
  OUTSIDE_BOUNDARY_CANDIDATE_OUTLINE_COLOR,
  promoteOutsideBoundaryAnnotations,
} from "../outsideBoundaryAnnotations";
import { OutsideBoundaryWarningDialog } from "../OutsideBoundaryWarningDialog";
import { getOutsideBoundarySessionChoice, setOutsideBoundarySessionChoice } from "../outsideBoundaryWarningSession";
import { useRetainedValue } from "@/hooks/useRetainedValue";

/** Shared empty set so resetting to "nothing hidden" is a no-op React can bail out of. */
const NO_HIDDEN_MEASUREMENT_KEYS: ReadonlySet<string> = new Set();
/** Shared empty list so an inactive review mode keeps a stable memo identity. */
const NO_REVIEW_OBJECTS: readonly ReviewObject[] = [];
const NO_HIDDEN_CLASS_NAMES: ReadonlySet<string> = new Set();
const EMPTY_ANNOTATION_CLASS_NAMES: string[] = [];
const EMPTY_MEASUREMENT_DESCRIPTORS: MeasurementDescriptor[] = [];

/** Depth-notice backgrounds: neutral while working, amber for a reduced mode, red for a failure. */
const DEPTH_NOTICE_TONE_CLASSES: Record<"info" | "warn" | "error", string> = {
  info: "bg-black/70",
  warn: "bg-amber-600/90",
  error: "bg-red-700/90",
};

type DraftBox = { start: Point; current: Point };

type PendingOutsideBoundaryBoxAction =
  | { kind: "create"; annotation: Annotation }
  | { kind: "update"; id: string; bbox: BBoxPixels; update: BoxKeypointUpdate };

/** One steppable object: its annotation id plus the box the stage zooms to. */
type ReviewObject = { id: string; bbox: BBoxPixels };

/** What the parent can drive on the review cursor. Reached through a ref from the arrow-key owner. */
export interface SegmentationReviewHandle {
  /**
   * Move the review cursor one object. Synchronous, so the arrow-key owner can decide within the
   * same key event whether to fall through to image navigation.
   */
  stepReviewObject: (direction: "prev" | "next") => ReviewStepResult;
}

type SegmentationProps = {
  imageUrl: string;
  /** Stable capture identity used to reset reversible, image-local edit state. */
  editorContextKey?: string;
  annotations: {
    list: Annotation[];
    measureBoundaryPixels?: Point[];
    annotationType: AnnotationType;
  };
  displayAnnotations?: boolean;
  showText?: boolean;
  /** View-only: hide the mask fill while keeping keypoints/lines. Never affects downloads. */
  showMask?: boolean;
  isEditMode?: boolean;
  boundaryAreaSquareMeters?: number;
  orientation: string;
  rawWidth: number;
  rawHeight: number;
  /** Resolved from the trait's objectDetectionDefaultOptions.containmentSuppression
   *  (GLE-79); undefined = the trait did not opt in and only plain-IoU NMS applies.
   *  Must be referentially stable — it keys the filter memo. */
  containmentParams?: ContainmentParams;
  measurementDescriptors?: MeasurementDescriptor[];
  /** Configured exact class names for this task/trait. */
  annotationClassNames?: string[];
  /** Task-wide class order used to keep shared names on the same color across traits. */
  annotationClassColorOrder?: string[];
  /** Stable task key used to retain class filters and active class while navigating images. */
  annotationClassStateKey?: string;
  /** Resolved depth evidence. Drives BOTH the edit-mode status pill and the
   *  derived editing mode (3D recompute / 2D position-only / locked), so the
   *  two can never disagree. */
  depthAvailability?: DepthAvailability | null;
  /** Asks the parent to re-attempt a retryable depth/metadata load. */
  onRetryDepthLoad?: () => void;
  /** Debug: mirror the depth map + mapped endpoints in a right-hand panel. */
  showDepthDebug?: boolean;
  onCloseDepthDebug?: () => void;
  onSegmentationAnnotationsChange?: (annotations: Annotation[]) => void;
  /** View-only mask toggle callback; drives the sidebar Display section. Never affects downloads. */
  onShowMaskChange?: (value: boolean) => void;
  /** Persisted filter panel to restore onto this image (thresholds + activated set). */
  initialFilterSettings?: SegmentationFilterSettings | null;
  /** Persisted parked measurement axes to restore onto this image. */
  initialHiddenMeasurementKeys?: readonly string[];
  /** Reports the fresh filter snapshot on every user-driven filter change. */
  onFilterSettingsChange?: (settings: SegmentationFilterSettings) => void;
  /** Reports the fresh parked-axis set on every user-driven toggle (and on exit-edit clear). */
  onHiddenMeasurementKeysChange?: (keys: string[]) => void;
  /** Edit-only: collapse every box to a center point (persisted view setting). */
  collapseToPoints?: boolean;
  /** Reports a Ctrl+B / sidebar-checkbox toggle of collapse mode. */
  onCollapseToPointsChange?: (value: boolean) => void;
  /** Edit-only control for accepting boundary-hidden detections into the working annotation set. */
  showOutsideBoundaryAnnotations?: boolean;
  onShowOutsideBoundaryAnnotationsChange?: (value: boolean) => void;
  /** Edit-only: zoom to one object at a time and step through them (persisted view setting). */
  reviewObjectsMode?: boolean;
  /** Which end of this image's object list to open on — "last" when the reviewer arrived backward. */
  initialReviewEdge?: ReviewLandingEdge;
  /**
   * Reports the review counter: the cursor's reading-order position and the number of reviewable
   * objects, or null while review mode is inactive. An index of -1 means the object being reviewed
   * was removed from view and the cursor is waiting for the next arrow key.
   */
  onReviewStatusChange?: (status: { index: number; total: number } | null) => void;
  /** Asks the parent to turn review mode off (a second Escape with nothing selected). */
  onExitReviewMode?: () => void;
};

/** A drawn box has no mask yet, so depth is sampled unmasked. */
const measureBoxWithDepth = (annotation: Annotation, depthFrame: DepthFrame): Annotation =>
  measureNewBoxAxes(annotation, (start, end) => measureDistanceCm(start, end, depthFrame));

const SegmentationComponent = (
  {
    imageUrl,
    editorContextKey = imageUrl,
    annotations,
    displayAnnotations,
    showText = true,
    showMask = true,
    isEditMode,
    boundaryAreaSquareMeters,
    rawWidth,
    rawHeight,
    containmentParams,
    measurementDescriptors = EMPTY_MEASUREMENT_DESCRIPTORS,
    annotationClassNames = EMPTY_ANNOTATION_CLASS_NAMES,
    annotationClassColorOrder = EMPTY_ANNOTATION_CLASS_NAMES,
    annotationClassStateKey,
    depthAvailability,
    onRetryDepthLoad,
    showDepthDebug,
    onCloseDepthDebug,
    onSegmentationAnnotationsChange,
    onShowMaskChange,
    initialFilterSettings,
    initialHiddenMeasurementKeys,
    onFilterSettingsChange,
    onHiddenMeasurementKeysChange,
    collapseToPoints = false,
    onCollapseToPointsChange,
    showOutsideBoundaryAnnotations = false,
    onShowOutsideBoundaryAnnotationsChange,
    reviewObjectsMode = false,
    initialReviewEdge = "first",
    onReviewStatusChange,
    onExitReviewMode,
  }: SegmentationProps,
  reviewRef: React.ForwardedRef<SegmentationReviewHandle>
) => {
  const { toast } = useToast();
  const [totalScale, setTotalScale] = useState(1);
  const [fitScale, setFitScale] = useState(1);
  const { viewport, onViewportChange } = useDepthDebugViewport(Boolean(showDepthDebug));
  // One source of truth: the editing mode is derived from the same depth
  // evidence the pill describes, so the two can never disagree.
  const measurementEditing = useMemo(() => deriveMeasurementEditingMode(depthAvailability), [depthAvailability]);
  // The debug panel mirrors the frame the drags are measured against, so it only
  // exists in 3D mode.
  const depthFrame = measurementEditing.kind === "3d" ? measurementEditing.depthFrame : null;
  const depthNotice = describeDepthAvailability(depthAvailability);
  // Lazy-init from the persisted snapshot so a restored filter is live on mount.
  // Values are kept raw (not clamped to this image's data maxima); the slider thumb
  // clamps visually while the filter semantics stay correct.
  const [confidenceThreshold, setConfidenceThreshold] = useState(() => initialFilterSettings?.confidenceThreshold ?? 0);
  const [maxLengthM, setMaxLengthM] = useState<number | null>(() => initialFilterSettings?.maxLengthM ?? null);
  const [maxCameraDistanceM, setMaxCameraDistanceM] = useState<number | null>(
    () => initialFilterSettings?.maxCameraDistanceM ?? null
  );
  const [maxBboxLengthPx, setMaxBboxLengthPx] = useState<number | null>(
    () => initialFilterSettings?.maxBboxLengthPx ?? null
  );
  const [maxOverlapIou, setMaxOverlapIou] = useState(() => initialFilterSettings?.maxOverlapIou ?? 1.0);
  const [activatedFilters, setActivatedFilters] = useState<Set<SegmentationFilterId>>(
    () => new Set(initialFilterSettings?.activated ?? [])
  );
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [hoverPoint, setHoverPoint] = useState<Point | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const [draftBox, setDraftBox] = useState<DraftBox | null>(null);
  const [pendingOutsideBoundaryAction, setPendingOutsideBoundaryAction] =
    useState<PendingOutsideBoundaryBoxAction | null>(null);
  const retainedPendingOutsideBoundaryAction = useRetainedValue(pendingOutsideBoundaryAction);
  const persistedClassNames = useMemo(
    () => annotations.list.map((annotation) => annotation.category?.name).filter(Boolean),
    [annotations.list]
  );
  const classRegistry = useMemo(
    () => buildAnnotationClassRegistry(annotationClassNames, persistedClassNames, annotationClassColorOrder),
    [annotationClassColorOrder, annotationClassNames, persistedClassNames]
  );
  const classSessionKey = `annotation-classes:${annotationClassStateKey ?? annotationClassNames.join("|")}`;
  const readClassSession = useCallback(() => {
    if (typeof window === "undefined") return null;
    try {
      return JSON.parse(window.sessionStorage.getItem(classSessionKey) ?? "null") as {
        activeClassName?: string;
        hiddenClassNames?: string[];
      } | null;
    } catch {
      return null;
    }
  }, [classSessionKey]);
  const [activeClassName, setActiveClassName] = useState(
    () => readClassSession()?.activeClassName ?? classRegistry.classes[0]?.name ?? "object"
  );
  const [hiddenClassNames, setHiddenClassNames] = useState<ReadonlySet<string>>(
    () => new Set(readClassSession()?.hiddenClassNames ?? [])
  );
  // Which measurement axes the user parked, so coincident handles can be reached.
  // Transient view state — never written to the annotation, Firestore, or exports.
  const [hiddenMeasurementKeys, setHiddenMeasurementKeys] = useState<ReadonlySet<string>>(() =>
    initialHiddenMeasurementKeys && initialHiddenMeasurementKeys.length > 0
      ? new Set(initialHiddenMeasurementKeys)
      : NO_HIDDEN_MEASUREMENT_KEYS
  );

  useEffect(() => {
    setActiveClassName((current) => resolveActiveAnnotationClass(current, classRegistry, hiddenClassNames));
  }, [classRegistry, hiddenClassNames]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      classSessionKey,
      JSON.stringify({
        activeClassName,
        hiddenClassNames: [...hiddenClassNames],
      })
    );
  }, [activeClassName, classSessionKey, hiddenClassNames]);

  const selectedAnnotation = useMemo(
    () => annotations.list.find((annotation) => annotation.id === selectedAnnotationId),
    [annotations.list, selectedAnnotationId]
  );
  const chooseActiveClass = useCallback((className: string) => {
    setActiveClassName(className);
    setHiddenClassNames((current) => {
      if (!current.has(className)) return current;
      const next = new Set(current);
      next.delete(className);
      return next;
    });
  }, []);

  const relabelSelectedAnnotation = useCallback(
    (className: string) => {
      if (!selectedAnnotationId) return;
      onSegmentationAnnotationsChange?.(
        annotations.list.map((annotation) =>
          annotation.id === selectedAnnotationId ? relabelBBoxAnnotation(annotation, className) : annotation
        )
      );
      setHiddenClassNames((current) => {
        if (!current.has(className)) return current;
        const next = new Set(current);
        next.delete(className);
        return next;
      });
    },
    [annotations.list, onSegmentationAnnotationsChange, selectedAnnotationId]
  );

  const toggleClassVisibility = useCallback(
    (className: string) => {
      const willHide = !hiddenClassNames.has(className);
      setHiddenClassNames((current) => {
        const next = new Set(current);
        if (!next.delete(className)) next.add(className);
        return next;
      });
      if (willHide && normalizeAnnotationClassName(selectedAnnotation?.category.name ?? "") === className) {
        setSelectedAnnotationId(null);
      }
    },
    [hiddenClassNames, selectedAnnotation]
  );

  const canDrawActiveClass = !classRegistry.isMultiClass || canDrawAnnotationClass(activeClassName, hiddenClassNames);

  const measurementAxisLegend = useMemo(
    () => buildMeasurementEndpointLegend(annotations.list, measurementDescriptors),
    [annotations.list, measurementDescriptors]
  );

  // Which landmark row the reviewer is pointing at in the sidebar. Narrowed to the
  // selected box at the render site below, exactly like `selected` — a bare name
  // would otherwise ring the same landmark on every annotation in the image.
  const [highlightedKeypointName, setHighlightedKeypointName] = useState<string | null>(null);

  const toggleMeasurementAxis = useCallback(
    (axisKey: string) => {
      const next = new Set(hiddenMeasurementKeys);
      if (!next.delete(axisKey)) next.add(axisKey);
      const resolved = next.size === 0 ? NO_HIDDEN_MEASUREMENT_KEYS : next;
      setHiddenMeasurementKeys(resolved);
      onHiddenMeasurementKeysChange?.(Array.from(resolved));
    },
    [hiddenMeasurementKeys, onHiddenMeasurementKeysChange]
  );

  // Drawable measurement axes OR stored values both count: the audited no-depth
  // captures carry complete keypoint pairs with no measurements block, and the
  // depth notice exists precisely for editing those. The legend already knows
  // which complete axis pairs exist, so reuse it instead of re-scanning.
  const hasMeasurementAnnotations = useMemo(
    () =>
      measurementAxisLegend.length > 0 ||
      annotations.list.some((annotation) => annotationHasMeasurementText(annotation.measurements)),
    [annotations.list, measurementAxisLegend]
  );

  const hasMask = useMemo(() => annotations.list.some((annotation) => annotation.segmentation), [annotations.list]);
  const hasBoxes = useMemo(() => annotations.list.some((annotation) => annotation.bbox), [annotations.list]);
  // Single source of truth for the edit-only collapse gate, so the checkbox, the
  // Ctrl+B hotkey, and the actual box→point render can't drift apart.
  const canCollapse = Boolean(isEditMode && hasBoxes && onCollapseToPointsChange);
  const collapseActive = canCollapse && collapseToPoints;

  /**
   * The landmarks of the SELECTED box, by axis key — empty with no selection.
   *
   * Derived from that one annotation rather than sliced out of the image-wide
   * legend, because both halves of a row are per-annotation facts: the persisted
   * name (a sibling may store the legacy `head` for the same axis) and the dot
   * colour (the canvas colours each box from its OWN axis set, so a box carrying a
   * subset lands on different slots than the union would give it). The union still
   * drives the axis rows themselves — parking an axis is image-wide.
   */
  const selectedAnnotationLandmarks = useMemo(
    () =>
      new Map(
        buildMeasurementEndpointLegend(selectedAnnotation ? [selectedAnnotation] : [], measurementDescriptors).map(
          ({ key, endpoints }) => [key, endpoints]
        )
      ),
    [measurementDescriptors, selectedAnnotation]
  );

  // What the reviewer can currently see, for keeping a hover callout readable rather
  // than merely inside the image. Held in a REF, not state: pan fires this on every
  // frame, and a state update would re-render all N annotation viewers each time. A
  // callout only exists while hovering or legend-highlighting, and both of those
  // already trigger a render, so the ref is always read fresh enough.
  //
  // Known limitation of the ref: wheel-panning WHILE a callout is open does not
  // re-render, so that one callout keeps its previous clamp until the next pointer
  // move. It still travels with the landmark it names (both are in image space), so
  // the worst case is a chip that is briefly clipped at an edge and re-clamps as soon
  // as the cursor moves. Making it exact costs a re-render of every box per pan frame.
  const visibleImageRectRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);
  const handleVisibleImageRectChange = useCallback((rect: { x: number; y: number; width: number; height: number }) => {
    visibleImageRectRef.current = rect;
  }, []);
  const getVisibleImageRect = useCallback(() => visibleImageRectRef.current, []);

  // A landmark row can vanish without ever firing blur — Ctrl+B collapses the boxes
  // while the row still holds keyboard focus, and switching boxes swaps the whole
  // list. Either way the highlight would survive its own row and strand a callout
  // on the canvas, so drop it whenever the rows the reviewer was pointing with go
  // away.
  useEffect(() => {
    setHighlightedKeypointName(null);
  }, [collapseActive, selectedAnnotationId]);

  const hasConfidenceData = useMemo(() => {
    const values = annotations.list
      .filter((annotation) => !isManualBBoxAnnotation(annotation))
      .map(({ confidence }) => confidence)
      .filter((c) => c !== undefined);
    return values.length > 0 && new Set(values).size > 1;
  }, [annotations.list]);

  const hasMaxLengthData = useMemo(() => {
    const lengths = annotations.list
      .map(({ measurements }) => measurements?.lengthM)
      .filter((l): l is number => l !== undefined);
    return lengths.length > 1 && new Set(lengths).size > 1;
  }, [annotations.list]);

  const hasMaxCameraDistanceData = useMemo(() => {
    const distances = annotations.list
      .map(({ measurements }) => measurements?.distanceFromCamM)
      .filter((d): d is number => d !== undefined);
    return distances.length > 1 && new Set(distances).size > 1;
  }, [annotations.list]);

  const bboxKeypointDistances = useMemo(
    () => computeMeasurementLengthPixelDistances(annotations.list),
    [annotations.list]
  );

  const hasMaxBboxLengthData = useMemo(() => {
    const lengths = bboxKeypointDistances.filter((l): l is number => l !== undefined);
    return lengths.length > 1 && new Set(lengths).size > 1;
  }, [bboxKeypointDistances]);

  const hasOverlapData = useMemo(() => {
    const eligible = annotations.list.filter((a) => a.bbox && a.segmentation && a.confidence !== undefined);
    return eligible.length >= 2;
  }, [annotations.list]);

  const maxLengthDataM = useMemo(() => {
    const lengths = annotations.list
      .map(({ measurements }) => measurements?.lengthM)
      .filter((l): l is number => l !== undefined);
    return lengths.length > 0 ? Math.max(...lengths) : null;
  }, [annotations.list]);

  const maxCameraDistanceDataM = useMemo(() => {
    const distances = annotations.list
      .map(({ measurements }) => measurements?.distanceFromCamM)
      .filter((d): d is number => d !== undefined);
    return distances.length > 0 ? Math.max(...distances) : null;
  }, [annotations.list]);

  const maxBboxLengthDataPx = useMemo(() => {
    const lengths = bboxKeypointDistances.filter((l): l is number => l !== undefined);
    return lengths.length > 0 ? Math.max(...lengths) : null;
  }, [bboxKeypointDistances]);

  useEffect(() => {
    if (!activatedFilters.has("maxLength") && maxLengthDataM !== null && maxLengthM !== maxLengthDataM) {
      setMaxLengthM(maxLengthDataM);
    }
  }, [activatedFilters, maxLengthDataM, maxLengthM]);

  useEffect(() => {
    if (
      !activatedFilters.has("cameraDistance") &&
      maxCameraDistanceDataM !== null &&
      maxCameraDistanceM !== maxCameraDistanceDataM
    ) {
      setMaxCameraDistanceM(maxCameraDistanceDataM);
    }
  }, [activatedFilters, maxCameraDistanceDataM, maxCameraDistanceM]);

  useEffect(() => {
    if (
      !activatedFilters.has("bboxLength") &&
      maxBboxLengthDataPx !== null &&
      maxBboxLengthPx !== maxBboxLengthDataPx
    ) {
      setMaxBboxLengthPx(maxBboxLengthDataPx);
    }
  }, [activatedFilters, maxBboxLengthDataPx, maxBboxLengthPx]);

  // Containment-aware overlap suppression is opt-in per trait (GLE-79) — not
  // slider-gated, mirroring iOS. Masks decode lazily (only for containment suspects) and
  // memoize on the segmentation object, which survives annotation write-backs
  // (the per-tick `{...annotation}` spreads keep the nested reference) and
  // auto-evicts when the image changes. Decode failures are cached too.
  const maskCacheRef = useRef(new WeakMap<SegmentationData, boolean[][] | null>());

  const filterThresholds = useMemo<SegmentationFilterThresholds>(
    () => ({ confidenceThreshold, maxLengthM, maxCameraDistanceM, maxBboxLengthPx, maxOverlapIou }),
    [confidenceThreshold, maxLengthM, maxCameraDistanceM, maxBboxLengthPx, maxOverlapIou]
  );

  const effectiveFilterThresholds = useMemo(
    () => resolveEffectiveFilterThresholds(filterThresholds, activatedFilters),
    [activatedFilters, filterThresholds]
  );

  const computeFilterReasons = useCallback(
    (candidateAnnotations: Annotation[], thresholds: SegmentationFilterThresholds) => {
      const getCandidateMask = (index: number): boolean[][] | null => {
        const segmentation = candidateAnnotations[index]?.segmentation;
        if (!segmentation) return null;
        const cache = maskCacheRef.current;
        if (!cache.has(segmentation)) {
          cache.set(segmentation, decodeCompressedRLEToMask(segmentation.compressedRLE));
        }
        return cache.get(segmentation) ?? null;
      };
      return computeFilterHiddenReasonsForList(
        candidateAnnotations,
        computeMeasurementLengthPixelDistances(candidateAnnotations),
        thresholds,
        containmentParams ? { params: containmentParams, getMask: getCandidateMask } : undefined
      );
    },
    [containmentParams]
  );

  const filterReasonsByIndex = useMemo(
    () => computeFilterReasons(annotations.list, effectiveFilterThresholds),
    [annotations.list, computeFilterReasons, effectiveFilterThresholds]
  );

  const normalizeFilterVisibility = useCallback(
    (
      candidateAnnotations: Annotation[],
      displayThresholds: SegmentationFilterThresholds,
      activeFilters: ReadonlySet<string>,
      preserveInactiveFilterReasons = false
    ) => {
      const effectiveThresholds = resolveEffectiveFilterThresholds(displayThresholds, activeFilters);
      return applyComputedFilterVisibility(
        candidateAnnotations,
        computeFilterReasons(candidateAnnotations, effectiveThresholds),
        preserveInactiveFilterReasons ? activeFilters : undefined
      );
    },
    [computeFilterReasons]
  );

  // Report the fresh filter snapshot upward on every user-driven change. Inactive
  // filter values auto-track the image data and carry no intent, so they are never
  // reported from the auto-track effects — only from applyFilterVisibility below.
  const reportFilterSettings = useCallback(
    (thresholds: SegmentationFilterThresholds, activeFilters: ReadonlySet<SegmentationFilterId>) => {
      onFilterSettingsChange?.({ ...thresholds, activated: Array.from(activeFilters) });
    },
    [onFilterSettingsChange]
  );

  const applyFilterVisibility = useCallback(
    (
      overrides: Partial<SegmentationFilterThresholds>,
      activeFilters: ReadonlySet<SegmentationFilterId> = activatedFilters,
      { report = true }: { report?: boolean } = {}
    ) => {
      // Merge with the exact undefined-vs-null semantics the sliders rely on:
      // an absent key means "keep current", an explicit null means "no limit".
      const nextThresholds: SegmentationFilterThresholds = {
        confidenceThreshold: overrides.confidenceThreshold ?? filterThresholds.confidenceThreshold,
        maxLengthM: overrides.maxLengthM !== undefined ? overrides.maxLengthM : filterThresholds.maxLengthM,
        maxCameraDistanceM:
          overrides.maxCameraDistanceM !== undefined
            ? overrides.maxCameraDistanceM
            : filterThresholds.maxCameraDistanceM,
        maxBboxLengthPx:
          overrides.maxBboxLengthPx !== undefined ? overrides.maxBboxLengthPx : filterThresholds.maxBboxLengthPx,
        maxOverlapIou: overrides.maxOverlapIou ?? filterThresholds.maxOverlapIou,
      };
      onSegmentationAnnotationsChange?.(normalizeFilterVisibility(annotations.list, nextThresholds, activeFilters));
      // nextThresholds is already the fully-merged snapshot, so reporting here keeps
      // the reported settings and the applied visibility in lockstep. The mount-time
      // restore passes { report: false } (a restore is not a user-driven change).
      if (report) reportFilterSettings(nextThresholds, activeFilters);
    },
    [
      activatedFilters,
      annotations.list,
      filterThresholds,
      normalizeFilterVisibility,
      onSegmentationAnnotationsChange,
      reportFilterSettings,
    ]
  );

  // One-shot restore: reflect a restored active filter into the saved annotation
  // list. Rendering already honors the restored thresholds via the visibility map,
  // so this write-back exists solely to mark the image dirty (updateSegmentationAnnotations
  // only flags dirty in edit mode) so Save persists the inherited filter. It therefore
  // runs ONLY in edit mode — firing it in preview would burn the one-shot guard without
  // flagging dirty, leaving a later Edit → Save reporting no changes (GLE round-4 P1),
  // and preview must stay view-only anyway (it never writes annotation data). Gating on
  // isEditMode makes the effect re-run and fire on the preview→edit transition (the
  // component stays mounted across the mode toggle). The parent mounts this component
  // with `list: []` and hydrates it from metadata in a LATER effect, so the write-back
  // must wait for real data before consuming its one shot (mirrors the KeyPoints restore
  // guard). A restore is not a user-driven change, so { report: false } suppresses the snapshot.
  const didRestoreWriteBackRef = useRef(false);
  useEffect(() => {
    if (didRestoreWriteBackRef.current) return;
    if (!isEditMode) return;
    if (!initialFilterSettings || initialFilterSettings.activated.length === 0) {
      didRestoreWriteBackRef.current = true;
      return;
    }
    if (annotations.list.length === 0) return;
    didRestoreWriteBackRef.current = true;
    applyFilterVisibility(
      {
        confidenceThreshold: initialFilterSettings.confidenceThreshold,
        maxLengthM: initialFilterSettings.maxLengthM,
        maxCameraDistanceM: initialFilterSettings.maxCameraDistanceM,
        maxBboxLengthPx: initialFilterSettings.maxBboxLengthPx,
        maxOverlapIou: initialFilterSettings.maxOverlapIou,
      },
      new Set(initialFilterSettings.activated),
      { report: false }
    );
  }, [annotations.list, applyFilterVisibility, initialFilterSettings, isEditMode]);

  const countedVisibilityMap = useMemo(() => {
    const map = new Map<number, { isVisible: boolean; isBelowThreshold: boolean }>();

    annotations.list.forEach((annotation, index) => {
      const isUserVisible = annotation.userVisibility?.isVisible !== false;
      const passesAllFilters = getBlockingFilterHiddenReasons(annotation, filterReasonsByIndex[index]).length === 0;
      // A filter-failing annotation renders only if the user explicitly force-showed
      // it (double-click re-enable writes manualOverride). Otherwise a box that is
      // user-visible but fails the active filter stays hidden, so the render matches
      // the count. This matters for a restored filter in preview: the edit-only
      // write-back no longer commits `isVisible: false` up front, so without the
      // manualOverride requirement a plain model detection failing the restored
      // filter would paint on screen while excluded from the count (GLE round-4 P1).
      const isBelowThreshold = isUserVisible && !passesAllFilters && annotation.userVisibility?.manualOverride === true;
      map.set(index, {
        isVisible: isUserVisible && passesAllFilters,
        isBelowThreshold,
      });
    });

    return map;
  }, [annotations.list, filterReasonsByIndex]);

  const hasValidBoundary = (annotations.measureBoundaryPixels?.length ?? 0) >= 3;
  const outsideBoundaryVisibilityBeforePromotionRef = useRef(new Map<string, Annotation["userVisibility"]>());
  useEffect(() => {
    outsideBoundaryVisibilityBeforePromotionRef.current.clear();
  }, [editorContextKey]);
  const visibilityMap = useMemo(() => {
    const map = new Map<number, { isVisible: boolean; isBelowThreshold: boolean }>();
    annotations.list.forEach((annotation, index) => {
      const counted = countedVisibilityMap.get(index) ?? { isVisible: false, isBelowThreshold: false };
      const outsideBoundaryCandidateState = getOutsideBoundaryCandidateState(annotation);
      const revealOutsideBoundaryCandidate =
        isEditMode &&
        displayAnnotations &&
        hasValidBoundary &&
        showOutsideBoundaryAnnotations &&
        filterReasonsByIndex[index].length === 0 &&
        outsideBoundaryCandidateState !== null;
      map.set(index, {
        // The toggle reveals boundary-hidden candidates. It never hides an accepted,
        // counted box merely because a client-side geometry check places it outside.
        isVisible: revealOutsideBoundaryCandidate || counted.isVisible,
        isBelowThreshold: counted.isBelowThreshold,
      });
    });
    return map;
  }, [
    annotations.list,
    countedVisibilityMap,
    displayAnnotations,
    filterReasonsByIndex,
    hasValidBoundary,
    isEditMode,
    showOutsideBoundaryAnnotations,
  ]);

  // Reconcile the one model filter that a human acceptance does not override:
  // overlap suppression. This also revives a previously suppressed acceptance
  // when its competing box is later removed or hidden.
  useEffect(() => {
    if (!isEditMode || !annotations.list.some(isAcceptedOutsideBoundaryAnnotation)) return;
    const normalized = applyComputedFilterVisibility(annotations.list, filterReasonsByIndex, activatedFilters);
    let changed = false;
    const reconciled = annotations.list.map((annotation, index) => {
      if (!isAcceptedOutsideBoundaryAnnotation(annotation) || normalized[index] === annotation) return annotation;
      changed = true;
      return normalized[index];
    });
    if (changed) onSegmentationAnnotationsChange?.(reconciled);
  }, [activatedFilters, annotations.list, filterReasonsByIndex, isEditMode, onSegmentationAnnotationsChange]);

  const classVisibleAnnotationIds = useMemo(
    () =>
      new Set(
        filterAnnotationsForClassView(
          annotations.list,
          classRegistry.isMultiClass ? hiddenClassNames : NO_HIDDEN_CLASS_NAMES
        ).map(({ id }) => id)
      ),
    [annotations.list, classRegistry.isMultiClass, hiddenClassNames]
  );

  useEffect(() => {
    if (!isEditMode) {
      outsideBoundaryVisibilityBeforePromotionRef.current.clear();
      return;
    }
    if (!hasValidBoundary) return;

    if (!showOutsideBoundaryAnnotations) {
      const demotedAnnotations = demoteOutsideBoundaryAnnotations(
        annotations.list,
        outsideBoundaryVisibilityBeforePromotionRef.current
      );
      outsideBoundaryVisibilityBeforePromotionRef.current.clear();
      if (demotedAnnotations !== annotations.list) {
        onSegmentationAnnotationsChange?.(
          normalizeFilterVisibility(demotedAnnotations, filterThresholds, activatedFilters, true)
        );
      }
      return;
    }

    if (!displayAnnotations) return;

    const acceptedIds = new Set(
      annotations.list.flatMap((annotation, index) =>
        filterReasonsByIndex[index].length === 0 &&
        classVisibleAnnotationIds.has(annotation.id) &&
        isAnnotationEligibleForOutsideBoundaryReveal(annotation)
          ? [annotation.id]
          : []
      )
    );
    if (acceptedIds.size === 0) return;

    annotations.list.forEach((annotation) => {
      if (acceptedIds.has(annotation.id) && !outsideBoundaryVisibilityBeforePromotionRef.current.has(annotation.id)) {
        outsideBoundaryVisibilityBeforePromotionRef.current.set(annotation.id, {
          ...annotation.userVisibility,
          ...(annotation.userVisibility.hiddenReasons
            ? { hiddenReasons: [...annotation.userVisibility.hiddenReasons] }
            : {}),
        });
      }
    });

    // Compose the active filters and outside-boundary acceptance in one update.
    // Persisted filters can restore in this same commit; using their normalized
    // list here ensures only boxes actually shown to the reviewer are accepted.
    const filteredAnnotations = normalizeFilterVisibility(annotations.list, filterThresholds, activatedFilters, true);
    const promotedAnnotations = promoteOutsideBoundaryAnnotations(filteredAnnotations, acceptedIds);
    onSegmentationAnnotationsChange?.(
      normalizeFilterVisibility(promotedAnnotations, filterThresholds, activatedFilters, true)
    );
  }, [
    activatedFilters,
    annotations.list,
    classVisibleAnnotationIds,
    displayAnnotations,
    filterReasonsByIndex,
    filterThresholds,
    hasValidBoundary,
    isEditMode,
    normalizeFilterVisibility,
    onSegmentationAnnotationsChange,
    showOutsideBoundaryAnnotations,
  ]);

  const visibleIndices = useMemo(() => {
    const indices: number[] = [];
    visibilityMap.forEach((visibility, index) => {
      const annotationId = annotations.list[index]?.id;
      if (visibility.isVisible && annotationId && classVisibleAnnotationIds.has(annotationId)) {
        indices.push(index);
      }
    });
    return new Set(indices);
  }, [annotations.list, classVisibleAnnotationIds, visibilityMap]);
  const countedIndices = useMemo(() => {
    const indices: number[] = [];
    countedVisibilityMap.forEach((visibility, index) => {
      const annotationId = annotations.list[index]?.id;
      if (visibility.isVisible && annotationId && classVisibleAnnotationIds.has(annotationId)) indices.push(index);
    });
    return new Set(indices);
  }, [annotations.list, classVisibleAnnotationIds, countedVisibilityMap]);
  const countedBeforeClassFilterCount = useMemo(
    () => countVisibleAnnotationsBeforeClassFilter(countedVisibilityMap),
    [countedVisibilityMap]
  );
  const classCounts = useMemo(
    () => countAnnotationsByClass(annotations.list, countedVisibilityMap),
    [annotations.list, countedVisibilityMap]
  );
  const classControls = classRegistry.isMultiClass ? (
    <AnnotationClassControls
      registry={classRegistry}
      counts={classCounts}
      selectedClassName={
        selectedAnnotation?.bbox ? normalizeAnnotationClassName(selectedAnnotation.category.name) : undefined
      }
      hiddenClassNames={hiddenClassNames}
      onToggleVisibility={toggleClassVisibility}
      onSelectedClassChange={relabelSelectedAnnotation}
    />
  ) : null;

  // Precompute bbox centers once per annotation list so double-click / hover
  // hit-testing is a tight, allocation-free scan. Hidden non-boundary boxes stay
  // hit-testable; outside-boundary candidates join the scan only when revealed.
  const toggleCandidates = useMemo<BBoxCandidate[]>(
    () =>
      annotations.list.flatMap((annotation, index) => {
        if ((!hasValidBoundary || !showOutsideBoundaryAnnotations) && getOutsideBoundaryCandidateState(annotation)) {
          return [];
        }
        return annotation.bbox && classVisibleAnnotationIds.has(annotation.id)
          ? [
              {
                index,
                bbox: annotation.bbox,
                confidence: annotation.confidence ?? 1,
                centerX: annotation.bbox.x + annotation.bbox.width / 2,
                centerY: annotation.bbox.y + annotation.bbox.height / 2,
              },
            ]
          : [];
      }),
    [annotations.list, classVisibleAnnotationIds, hasValidBoundary, showOutsideBoundaryAnnotations]
  );

  // Hover highlights only currently-rendered boxes, so hovering doesn't flash
  // previews of filter-hidden or toggled-off boxes.
  const hoverCandidates = useMemo<BBoxCandidate[]>(
    () =>
      toggleCandidates.filter((candidate) => {
        const visibility = visibilityMap.get(candidate.index);
        return Boolean(
          visibility &&
            (visibility.isVisible || visibility.isBelowThreshold) &&
            classVisibleAnnotationIds.has(annotations.list[candidate.index]?.id)
        );
      }),
    [annotations.list, classVisibleAnnotationIds, toggleCandidates, visibilityMap]
  );

  // ── Review-objects mode ────────────────────────────────────────────────────────────────────
  // A cursor that walks the visible objects in reading order, zooming the stage to each one. It is
  // deliberately NOT the same thing as `selectedAnnotationId`: selection is cleared by all sorts of
  // incidental interactions (a press on empty space, a double-click, a filter change), and losing
  // the reviewer's place to any of those would make the mode unusable. Selection follows the
  // cursor; the cursor never follows a deselect.
  const reviewActive = Boolean(isEditMode && displayAnnotations && reviewObjectsMode);
  const [reviewCursorId, setReviewCursorId] = useState<string | null>(null);
  const [reviewFocusRequest, setReviewFocusRequest] = useState<StageFocusRequest | null>(null);
  // Mirrors `reviewCursorId` for synchronous reads: a held arrow key must not step twice onto the
  // same object within one frame, and the imperative handle has to read the truth mid-event.
  const reviewCursorRef = useRef<string | null>(null);
  // The reading-order slot the cursor last occupied. Survives the cursor going inert, so an arrow
  // key can resume from where the removed object used to be.
  const lastReviewIndexRef = useRef(0);

  /** Only currently-rendered boxes are steppable. */
  const reviewObjects = useMemo<readonly ReviewObject[]>(() => {
    if (!reviewActive) return NO_REVIEW_OBJECTS;
    return orderCandidatesForReview(toggleCandidates.filter((candidate) => visibleIndices.has(candidate.index))).map(
      (candidate) => ({ id: annotations.list[candidate.index].id, bbox: candidate.bbox })
    );
  }, [annotations.list, reviewActive, toggleCandidates, visibleIndices]);

  const reviewCursorIndex = useMemo(
    () => (reviewCursorId ? reviewObjects.findIndex((object) => object.id === reviewCursorId) : -1),
    [reviewCursorId, reviewObjects]
  );

  /** The only way to move the cursor: ref and state are written together so they can't disagree. */
  const setReviewCursor = useCallback((id: string | null) => {
    reviewCursorRef.current = id;
    setReviewCursorId(id);
  }, []);

  /** Ask the stage to jump. The token must change on EVERY request — see `StageFocusRequest`. */
  const requestStageFocus = useCallback((bbox: BBoxPixels | null) => {
    setReviewFocusRequest((previous) => ({ bbox, token: (previous?.token ?? 0) + 1 }));
  }, []);

  /** Point the cursor and the selection at an object and fly the stage to it. */
  const focusReviewObject = useCallback(
    (target: ReviewObject) => {
      setReviewCursor(target.id);
      setSelectedAnnotationId(target.id);
      const index = reviewObjects.findIndex((object) => object.id === target.id);
      if (index >= 0) lastReviewIndexRef.current = index;
      requestStageFocus(target.bbox);
    },
    [requestStageFocus, reviewObjects, setReviewCursor]
  );

  const stepReviewObject = useCallback(
    (direction: "prev" | "next"): ReviewStepResult => {
      if (!reviewActive) return "inactive";
      if (reviewObjects.length === 0) return "empty";

      const cursorIndex = reviewCursorRef.current
        ? reviewObjects.findIndex((object) => object.id === reviewCursorRef.current)
        : -1;
      // No cursor means the object we were on was removed. Resume from the slot it held.
      const isInert = cursorIndex < 0;
      const anchor = isInert ? lastReviewIndexRef.current : cursorIndex;

      let target: number;
      if (direction === "prev") {
        target = anchor - 1;
      } else if (isInert) {
        // Land ON the vacated slot — whatever took the removed object's place — not past it.
        target = anchor;
      } else {
        target = anchor + 1;
      }
      if (target < 0 || target >= reviewObjects.length) return "boundary";

      focusReviewObject(reviewObjects[target]);
      return "moved";
    },
    [focusReviewObject, reviewActive, reviewObjects]
  );

  useImperativeHandle(reviewRef, () => ({ stepReviewObject }), [stepReviewObject]);

  // Open on an object as soon as review mode turns on and the annotations have resolved. One rule
  // serves both cases: a fresh mount has nothing selected and uses the arrival edge, while toggling
  // the mode on mid-image keeps whatever box the reviewer already had selected.
  const didFocusReviewOnActivationRef = useRef(false);
  useEffect(() => {
    if (!reviewActive) {
      didFocusReviewOnActivationRef.current = false;
      return;
    }
    if (didFocusReviewOnActivationRef.current || reviewObjects.length === 0) return;
    didFocusReviewOnActivationRef.current = true;
    const preferred = reviewObjects.find((object) => object.id === selectedAnnotationId);
    const edge = initialReviewEdge === "last" ? reviewObjects[reviewObjects.length - 1] : reviewObjects[0];
    focusReviewObject(preferred ?? edge);
  }, [focusReviewObject, initialReviewEdge, reviewActive, reviewObjects, selectedAnnotationId]);

  // The reviewed object can vanish under the cursor — a filter slider moves, or it gets deleted.
  // Go INERT rather than auto-advancing: the stage stays exactly where the reviewer left it (never
  // snapping back to the whole image) and the counter reports -1 until an arrow key resumes.
  useEffect(() => {
    if (!reviewActive) {
      setReviewCursor(null);
      lastReviewIndexRef.current = 0;
      return;
    }
    if (!reviewCursorId || reviewObjects.some((object) => object.id === reviewCursorId)) return;
    setReviewCursor(null);
  }, [reviewActive, reviewCursorId, reviewObjects, setReviewCursor]);

  useEffect(() => {
    onReviewStatusChange?.(reviewActive ? { index: reviewCursorIndex, total: reviewObjects.length } : null);
  }, [onReviewStatusChange, reviewActive, reviewCursorIndex, reviewObjects.length]);

  // Turning review mode OFF returns to the whole image — seeing the full picture again is the
  // reason to turn it off. Keyed on the prop, not on `reviewActive`, so merely leaving edit mode
  // leaves the reviewer's zoom alone.
  const previousReviewObjectsModeRef = useRef(reviewObjectsMode);
  useEffect(() => {
    const wasOn = previousReviewObjectsModeRef.current;
    previousReviewObjectsModeRef.current = reviewObjectsMode;
    if (wasOn && !reviewObjectsMode) requestStageFocus(null);
  }, [requestStageFocus, reviewObjectsMode]);

  /**
   * Selection entry point for clicks. In review mode a click on a steppable box also moves the
   * counter to it — but never re-zooms, or the stage would jump away from what was just clicked.
   */
  const selectAnnotation = useCallback(
    (id: string | null) => {
      setSelectedAnnotationId(id);
      if (!id || !reviewActive) return;
      const index = reviewObjects.findIndex((object) => object.id === id);
      if (index < 0) return;
      lastReviewIndexRef.current = index;
      setReviewCursor(id);
    },
    [reviewActive, reviewObjects, setReviewCursor]
  );

  const setAnnotationVisibility = useCallback(
    (id: string, isVisible: boolean, hiddenReasons?: HiddenReason[]) => {
      onSegmentationAnnotationsChange?.(
        annotations.list.map((annotation) => {
          if (annotation.id !== id) return annotation;

          const existingReasons = annotation.userVisibility?.hiddenReasons ?? [];
          if (isVisible && existingReasons.includes("userToggledOff")) {
            const restoredReasons = existingReasons.filter((reason) => reason !== "userToggledOff");
            return {
              ...annotation,
              userVisibility:
                restoredReasons.length === 0
                  ? { isVisible: true, manualOverride: true }
                  : {
                      isVisible: false,
                      manualOverride: true,
                      hiddenReasons: restoredReasons,
                    },
            };
          }

          const nextHiddenReasons = isVisible
            ? []
            : [...new Set<HiddenReason>([...existingReasons, ...(hiddenReasons ?? [])])];
          return {
            ...annotation,
            userVisibility: {
              isVisible,
              manualOverride: true,
              ...(nextHiddenReasons.length > 0 ? { hiddenReasons: nextHiddenReasons } : {}),
            },
          };
        })
      );
    },
    [annotations.list, onSegmentationAnnotationsChange]
  );

  const toggleAnnotationVisibility = useCallback(
    (index: number) => {
      const annotation = annotations.list[index];
      if (!annotation) return;

      const visibility = visibilityMap.get(index);
      if (!visibility) return;

      const isCurrentlyVisible = visibility.isVisible || visibility.isBelowThreshold;
      const newVisibility = getOutsideBoundaryCandidateState(annotation) === "rejected" ? true : !isCurrentlyVisible;
      setAnnotationVisibility(annotation.id, newVisibility, newVisibility ? undefined : ["userToggledOff"]);
    },
    [annotations.list, setAnnotationVisibility, visibilityMap]
  );

  const onConfidenceThresholdChange = useCallback(
    (threshold: number) => {
      const nextActivatedFilters = new Set(activatedFilters).add("sensitivity");
      setConfidenceThreshold(threshold);
      setActivatedFilters(nextActivatedFilters);
      applyFilterVisibility({ confidenceThreshold: threshold }, nextActivatedFilters);
    },
    [activatedFilters, applyFilterVisibility]
  );

  const onMaxLengthChange = useCallback(
    (length: number) => {
      const nextActivatedFilters = new Set(activatedFilters).add("maxLength");
      setMaxLengthM(length);
      setActivatedFilters(nextActivatedFilters);
      applyFilterVisibility({ maxLengthM: length }, nextActivatedFilters);
    },
    [activatedFilters, applyFilterVisibility]
  );

  const onMaxCameraDistanceChange = useCallback(
    (distance: number) => {
      const nextActivatedFilters = new Set(activatedFilters).add("cameraDistance");
      setMaxCameraDistanceM(distance);
      setActivatedFilters(nextActivatedFilters);
      applyFilterVisibility({ maxCameraDistanceM: distance }, nextActivatedFilters);
    },
    [activatedFilters, applyFilterVisibility]
  );

  const onMaxBboxLengthChange = useCallback(
    (length: number) => {
      const nextActivatedFilters = new Set(activatedFilters).add("bboxLength");
      setMaxBboxLengthPx(length);
      setActivatedFilters(nextActivatedFilters);
      applyFilterVisibility({ maxBboxLengthPx: length }, nextActivatedFilters);
    },
    [activatedFilters, applyFilterVisibility]
  );

  const onMaxOverlapIouChange = useCallback(
    (iou: number) => {
      const nextActivatedFilters = new Set(activatedFilters).add("maxOverlapIou");
      setMaxOverlapIou(iou);
      setActivatedFilters(nextActivatedFilters);
      applyFilterVisibility({ maxOverlapIou: iou }, nextActivatedFilters);
    },
    [activatedFilters, applyFilterVisibility]
  );

  // Reset every filter back to its "no filtering" default and mark them all
  // inactive, restoring all filter-hidden annotations (user-toggled-off boxes stay off).
  const resetFilters = useCallback(() => {
    const defaults = {
      confidenceThreshold: 0,
      maxLengthM: maxLengthDataM,
      maxCameraDistanceM: maxCameraDistanceDataM,
      maxBboxLengthPx: maxBboxLengthDataPx,
      maxOverlapIou: 1.0,
    };
    setConfidenceThreshold(defaults.confidenceThreshold);
    setMaxLengthM(defaults.maxLengthM);
    setMaxCameraDistanceM(defaults.maxCameraDistanceM);
    setMaxBboxLengthPx(defaults.maxBboxLengthPx);
    setMaxOverlapIou(defaults.maxOverlapIou);
    const noActiveFilters = new Set<SegmentationFilterId>();
    setActivatedFilters(noActiveFilters);
    applyFilterVisibility(defaults, noActiveFilters);
  }, [applyFilterVisibility, maxBboxLengthDataPx, maxCameraDistanceDataM, maxLengthDataM]);

  const displayBoundaryPixels = useMemo(
    () => getSegmentationDisplayBoundaryPixels(annotations.measureBoundaryPixels),
    [annotations.measureBoundaryPixels]
  );

  const normalizeBox = (box: DraftBox) => ({
    x: Math.min(box.start.x, box.current.x),
    y: Math.min(box.start.y, box.current.y),
    width: Math.abs(box.current.x - box.start.x),
    height: Math.abs(box.current.y - box.start.y),
  });

  // Boxes drawn this session before depth was ready. Only these are measured on
  // arrival — opening an image must never rewrite boxes someone already saved.
  const awaitingDepthBoxIdsRef = useRef<Set<string>>(new Set());

  const createManualAnnotation = useCallback(
    (bbox: { x: number; y: number; width: number; height: number }): Annotation => {
      // Seed the trait's landmarks so the box is measurable the moment it is
      // drawn — the editor can only drag a point that already exists, and the
      // axis set comes from the trait config, not from what the image happens
      // to contain. A counting trait defines none, and gets none.
      const seededKeypoints = buildSeedKeypointsForBox(measurementDescriptors, bbox);
      const created: Annotation = {
        id: generateManualAnnotationId(),
        category: classRegistry.isMultiClass
          ? { name: activeClassName }
          : (annotations.list.find((annotation) => annotation.category)?.category ?? { name: "object" }),
        bbox,
        ...(seededKeypoints.length > 0 ? { keypoints: seededKeypoints, numKeypoints: seededKeypoints.length } : {}),
        confidence: 1,
        source: { humanAnnotated: {} },
        userVisibility: { isVisible: true, manualOverride: true },
        userAdjustment: { manuallyCreated: true },
        createdAt: new Date().toISOString(),
      };
      // Value the seeded axes on release, so the box is measured — labeled and
      // counted in the capture's averages — without the user having to drag an
      // axis first. Drawing never waits on depth: until it is ready the box
      // shows unmeasured axes and is measured the moment depth arrives.
      if (measurementEditing.kind !== "3d") {
        if (seededKeypoints.length > 0) awaitingDepthBoxIdsRef.current.add(created.id);
        return created;
      }
      return measureBoxWithDepth(created, measurementEditing.depthFrame);
    },
    [activeClassName, annotations.list, classRegistry.isMultiClass, measurementDescriptors, measurementEditing]
  );

  // Measure the boxes drawn before depth was ready, once it is.
  useEffect(() => {
    const awaiting = awaitingDepthBoxIdsRef.current;
    if (measurementEditing.kind !== "3d" || awaiting.size === 0) return;
    const { depthFrame } = measurementEditing;
    let changed = false;
    const next = annotations.list.map((annotation) => {
      if (!awaiting.has(annotation.id)) return annotation;
      // Forget only boxes actually seen, in case the parent has not yet
      // propagated one drawn in the same tick.
      awaiting.delete(annotation.id);
      const measured = measureBoxWithDepth(annotation, depthFrame);
      if (measured !== annotation) changed = true;
      return measured;
    });
    if (changed) onSegmentationAnnotationsChange?.(next);
  }, [annotations.list, measurementEditing, onSegmentationAnnotationsChange]);

  const appendManualAnnotation = useCallback(
    (manualAnnotation: Annotation) => {
      onSegmentationAnnotationsChange?.([...annotations.list, manualAnnotation]);
      setSelectedAnnotationId(manualAnnotation.id);
      // The new box isn't in `reviewObjects` yet — the parent owns the list — but it is visible by
      // construction, so adopt it as the cursor directly. No zoom: the reviewer is already there.
      if (reviewActive) setReviewCursor(manualAnnotation.id);
    },
    [annotations.list, onSegmentationAnnotationsChange, reviewActive, setReviewCursor]
  );

  const applyManualBBoxUpdate = useCallback(
    (id: string, bbox: BBoxPixels, update: BoxKeypointUpdate) => {
      onSegmentationAnnotationsChange?.(
        annotations.list.map((annotation) => {
          if (annotation.id !== id || !isManualBBoxAnnotation(annotation)) return annotation;
          // The viewer owns geometry and depth; this layer only owns annotation
          // state. Dropping `update` here would move the box and strand its
          // landmarks — which type-checks, so a test has to hold the line.
          const moved = applyBoxChangeToKeypoints(annotation, bbox, update);
          return { ...moved, userAdjustment: { ...moved.userAdjustment, manuallyCreated: true } };
        })
      );
    },
    [annotations.list, onSegmentationAnnotationsChange]
  );

  const commitOutsideBoundaryAction = useCallback(
    (action: PendingOutsideBoundaryBoxAction) => {
      if (action.kind === "create") appendManualAnnotation(action.annotation);
      else applyManualBBoxUpdate(action.id, action.bbox, action.update);
    },
    [appendManualAnnotation, applyManualBBoxUpdate]
  );

  const requestBoxMutation = useCallback(
    (action: PendingOutsideBoundaryBoxAction) => {
      const bbox = action.kind === "create" ? action.annotation.bbox : action.bbox;
      if (!bbox || !hasValidBoundary || !isBBoxOutsideMeasureBoundary(bbox, displayBoundaryPixels)) {
        commitOutsideBoundaryAction(action);
        return;
      }

      const outsideBoundaryChoice = getOutsideBoundarySessionChoice("bbox");
      if (outsideBoundaryChoice === "allow") {
        commitOutsideBoundaryAction(action);
        return;
      }
      setPendingOutsideBoundaryAction(action);
    },
    [commitOutsideBoundaryAction, displayBoundaryPixels, hasValidBoundary]
  );

  // Box bodies don't listen, so every press that isn't on a measurement handle (or a
  // selected manual box's body) lands here. Always begin a draft — a press inside an
  // existing box may still be the start of a new box drawn over it. mouseup decides
  // whether it was a draw or a click.
  const handleImageMouseDown = useCallback(
    (point: Point) => {
      if (!isEditMode || !displayAnnotations) return;
      setSelectedAnnotationId(null);
      setDraftBox({ start: point, current: point });
    },
    [displayAnnotations, isEditMode]
  );

  const handleImageDoubleClick = useCallback(
    (point: Point) => {
      if (!isEditMode || !displayAnnotations) return;
      // Prefer the rendered box the hover highlight indicates; only fall back to
      // hidden boxes (re-enable) when no rendered box contains the click.
      const target = resolveDoubleClickTargetIndex(point, hoverCandidates, toggleCandidates);
      if (target === -1) return;
      toggleAnnotationVisibility(target);
      // Clear the hover so a box toggled off disappears immediately instead of
      // lingering as a hover preview until the next mouse move.
      setHoveredIndex(-1);
      // The double-click's constituent clicks select whichever hit-rect Konva put
      // on top, which may differ from the closest-to-center box we just toggled.
      // Clear selection so a follow-up Delete/drag can't act on the wrong box.
      setSelectedAnnotationId(null);
    },
    [displayAnnotations, isEditMode, toggleAnnotationVisibility, hoverCandidates, toggleCandidates]
  );

  const handleImageMouseMove = useCallback(
    (point: Point) => {
      if (!isEditMode || !displayAnnotations) return;
      setHoverPoint(point);
      setDraftBox((currentDraft) => (currentDraft ? { ...currentDraft, current: point } : currentDraft));
      // Highlight the rendered box a double-click would toggle (none while drawing).
      // Skip the state update when the target is unchanged to avoid re-render churn.
      const nextHovered = draftBox ? -1 : findToggleTargetIndex(point, hoverCandidates);
      setHoveredIndex((prev) => (prev === nextHovered ? prev : nextHovered));
    },
    [displayAnnotations, isEditMode, draftBox, hoverCandidates]
  );

  const handleImageMouseUp = useCallback(
    (point: Point) => {
      if (!draftBox || !isEditMode || !displayAnnotations) return;
      const nextBox = normalizeBox({ ...draftBox, current: point });
      setDraftBox(null);

      if (!isDraftBoxDrawable(nextBox)) {
        // The press barely moved: it was a click. Resolve selection by closest-to-center
        // from where the press STARTED, the same rule hover and double-click use — so the
        // box the hover highlighted is the box that gets selected.
        const target = findToggleTargetIndex(draftBox.start, hoverCandidates);
        selectAnnotation(target === -1 ? null : (annotations.list[target]?.id ?? null));
        return;
      }

      if (!canDrawActiveClass) return;
      const manualAnnotation = createManualAnnotation(nextBox);
      requestBoxMutation({ kind: "create", annotation: manualAnnotation });
    },
    [
      annotations.list,
      canDrawActiveClass,
      hoverCandidates,
      createManualAnnotation,
      displayAnnotations,
      draftBox,
      isEditMode,
      requestBoxMutation,
      selectAnnotation,
    ]
  );

  const handleImageMouseLeave = useCallback(() => {
    setHoverPoint(null);
    setHoveredIndex(-1);
    setDraftBox(null);
  }, []);

  const updateManualBBox = useCallback(
    (id: string, bbox: BBoxPixels, update: BoxKeypointUpdate) =>
      requestBoxMutation({ kind: "update", id, bbox, update }),
    [requestBoxMutation]
  );

  const updateMeasurementKeypoint = useCallback(
    (id: string, keypointName: MeasurementKeypointName, x: number, y: number, recomputedMeters: number | null) => {
      // 2D is decided by the capture's depth evidence, never inferred from a null
      // recompute — a failed 3D recompute must still snap back rather than
      // silently clear the value.
      const editedAnnotations = annotations.list.map((annotation) => {
        if (annotation.id !== id) return annotation;
        return measurementEditing.kind === "2d"
          ? applyGeometryOnlyKeypointEdit(annotation, keypointName, x, y)
          : applyMeasurementKeypointEdit(annotation, keypointName, x, y, recomputedMeters);
      });
      const normalizedAnnotations = normalizeFilterVisibility(
        editedAnnotations,
        filterThresholds,
        activatedFilters,
        true
      );
      onSegmentationAnnotationsChange?.(normalizedAnnotations);
      if (normalizedAnnotations.find((annotation) => annotation.id === id)?.userVisibility?.isVisible === false) {
        setSelectedAnnotationId(null);
      }
    },
    [
      activatedFilters,
      annotations.list,
      filterThresholds,
      measurementEditing,
      normalizeFilterVisibility,
      onSegmentationAnnotationsChange,
    ]
  );

  const [depthDebugDraft, setDepthDebugDraft] = useState<MeasurementDragDraft | null>(null);
  const selectedDepthDebugAnnotation = useMemo(
    () => annotations.list.find((annotation) => annotation.id === selectedAnnotationId) ?? null,
    [annotations.list, selectedAnnotationId]
  );
  const handleMeasurementDraft = useCallback((draft: MeasurementDragDraft | null) => setDepthDebugDraft(draft), []);
  const handleInvalidMeasurement = useCallback(() => {
    toast({
      title: "Measurement unchanged",
      description: "No valid depth was found at that point, so the handle returned to its previous position.",
      variant: "destructive",
    });
  }, [toast]);

  const measurementHudCorner = useMemo(
    () =>
      depthDebugDraft
        ? selectMeasurementHudCorner({
            imageWidth: rawWidth,
            imageHeight: rawHeight,
            bbox: depthDebugDraft.bbox,
            pointer: { x: depthDebugDraft.x, y: depthDebugDraft.y },
            topRightUnavailable: Boolean(showDepthDebug),
          })
        : "top-right",
    [depthDebugDraft, rawHeight, rawWidth, showDepthDebug]
  );

  const deleteSelectedAnnotation = useCallback(() => {
    if (!selectedAnnotationId) return;
    const selectedAnnotation = annotations.list.find((annotation) => annotation.id === selectedAnnotationId);
    if (!selectedAnnotation) return;

    if (isManualBBoxAnnotation(selectedAnnotation)) {
      onSegmentationAnnotationsChange?.(
        annotations.list.filter((annotation) => annotation.id !== selectedAnnotationId)
      );
    } else {
      setAnnotationVisibility(selectedAnnotationId, false, ["userToggledOff"]);
    }
    setSelectedAnnotationId(null);
  }, [annotations.list, onSegmentationAnnotationsChange, selectedAnnotationId, setAnnotationVisibility]);

  useEffect(() => {
    if (!isEditMode || !displayAnnotations) {
      setSelectedAnnotationId(null);
      setDraftBox(null);
      setHoverPoint(null);
      setHoveredIndex(-1);
      setDepthDebugDraft(null);
    }
  }, [displayAnnotations, isEditMode]);

  // The toggles are an edit-mode aid: clear on the transition OUT of edit mode (and
  // report the clear so the session snapshot empties too) — a per-image remount covers
  // image changes, and a restored value must survive the initial mount rather than be
  // wiped by it. One consequence of persistence: a viewer closed mid-edit keeps its
  // parked axes in the session, so a later image opened in preview can mount with them
  // still dimmed until edit mode is entered and exited. Stale keys for axes a capture
  // doesn't have are inert (dimming keys off the legend, which only lists real axes).
  const previousIsEditModeRef = useRef(isEditMode);
  useEffect(() => {
    const wasEditMode = previousIsEditModeRef.current;
    previousIsEditModeRef.current = isEditMode;
    if (wasEditMode && !isEditMode) {
      setHiddenMeasurementKeys(NO_HIDDEN_MEASUREMENT_KEYS);
      onHiddenMeasurementKeysChange?.([]);
    }
  }, [isEditMode, onHiddenMeasurementKeysChange]);

  useEffect(() => {
    setDepthDebugDraft(null);
  }, [displayAnnotations, imageUrl, selectedAnnotationId]);

  useEffect(() => {
    if (!selectedAnnotationId) return;
    const selectedIndex = annotations.list.findIndex((annotation) => annotation.id === selectedAnnotationId);
    if (selectedIndex >= 0 && isAnnotationEffectivelyVisible(visibilityMap.get(selectedIndex))) {
      return;
    }
    setSelectedAnnotationId(null);
  }, [annotations.list, selectedAnnotationId, visibilityMap]);

  useEffect(() => {
    if (!isEditMode || !displayAnnotations) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreSequenceNavigationKeys(event.target)) return;
      const shortcutClass = shouldHandleAnnotationClassShortcut(event, displayAnnotations)
        ? getAnnotationClassShortcut(event.key, classRegistry)
        : undefined;
      if (shortcutClass && classRegistry.isMultiClass) {
        event.preventDefault();
        if (selectedAnnotationId) relabelSelectedAnnotation(shortcutClass.name);
        else chooseActiveClass(shortcutClass.name);
        return;
      }
      if (event.key === "Escape") {
        // Escape backs out one level at a time and never closes the editor (the viewer Dialog
        // suppresses its Escape-to-close while editing): first the selection, then review mode
        // itself — which returns to the whole image. Outside review mode with nothing selected
        // this effect isn't attached, so Escape does nothing at all.
        event.preventDefault();
        if (selectedAnnotationId) {
          setSelectedAnnotationId(null);
          return;
        }
        if (reviewActive) onExitReviewMode?.();
        return;
      }
      if (!selectedAnnotationId) return;
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (!selectedAnnotationId) return;
      event.preventDefault();
      deleteSelectedAnnotation();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    chooseActiveClass,
    classRegistry,
    deleteSelectedAnnotation,
    displayAnnotations,
    isEditMode,
    onExitReviewMode,
    relabelSelectedAnnotation,
    reviewActive,
    selectedAnnotationId,
  ]);

  // Ctrl/Cmd+B toggles collapse-to-points (edit mode, when there are boxes to collapse).
  // preventDefault stops the browser's bold shortcut; input/slider targets are ignored.
  useEffect(() => {
    if (!canCollapse || !displayAnnotations) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "b") return;
      if (shouldIgnoreSequenceNavigationKeys(event.target)) return;
      event.preventDefault();
      onCollapseToPointsChange?.(!collapseToPoints);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canCollapse, collapseToPoints, displayAnnotations, onCollapseToPointsChange]);

  const activeDraftBox = draftBox && canDrawActiveClass ? normalizeBox(draftBox) : null;
  const activeClassColor = getAnnotationClassColor(classRegistry, activeClassName);

  return (
    <div className="flex flex-1 min-h-0 w-full">
      {displayAnnotations && isEditMode && classRegistry.isMultiClass && (
        <aside
          data-testid="new-box-class-rail"
          className="flex h-full w-44 shrink-0 flex-col border-r border-slate-200 bg-slate-50/95 p-4 text-slate-700">
          <NewBoxClassControl
            registry={classRegistry}
            activeClassName={activeClassName}
            drawingDisabled={!canDrawActiveClass}
            onActiveClassChange={chooseActiveClass}
          />
        </aside>
      )}
      <div className="relative flex-1 min-w-0">
        <AnnotationViewerBase
          onlyImage={!displayAnnotations}
          imageUrl={imageUrl}
          onDoubleClick={handleImageDoubleClick}
          onImageMouseDown={handleImageMouseDown}
          onImageMouseMove={handleImageMouseMove}
          onImageMouseUp={handleImageMouseUp}
          onImageMouseLeave={handleImageMouseLeave}
          cursorClassName={
            isEditMode && displayAnnotations ? (canDrawActiveClass ? "cursor-crosshair" : "cursor-default") : undefined
          }
          disableStageDrag={isEditMode && displayAnnotations}
          captureDragOutsideImage={Boolean(isEditMode && displayAnnotations)}
          focusRequest={reviewFocusRequest}
          onZoomChange={setTotalScale}
          onFitScaleChange={setFitScale}
          onViewportChange={onViewportChange}
          onVisibleImageRectChange={handleVisibleImageRectChange}>
          {displayBoundaryPixels.length > 0 && (
            <Line
              points={displayBoundaryPixels.flatMap(({ x, y }) => [x, y])}
              closed
              fill="transparent"
              stroke="yellow"
              strokeWidth={10 / totalScale}
            />
          )}

          {annotations.list.map((annotation, index) => {
            const visibility = visibilityMap.get(index);
            if (!visibility) return null;
            if (!classVisibleAnnotationIds.has(annotation.id)) return null;
            const resolvedColors = resolveSegmentationAnnotationColors(
              annotation,
              index,
              classRegistry,
              annotationClassNames
            );
            const outsideBoundaryCandidateState =
              isEditMode && displayAnnotations && hasValidBoundary && showOutsideBoundaryAnnotations
                ? getOutsideBoundaryCandidateState(annotation)
                : null;
            const color = outsideBoundaryCandidateState ? OUTSIDE_BOUNDARY_CANDIDATE_COLOR : resolvedColors.color;
            const outlineColor = outsideBoundaryCandidateState
              ? OUTSIDE_BOUNDARY_CANDIDATE_OUTLINE_COLOR
              : resolvedColors.outlineColor;

            return (
              <SegmentationViewer
                isVisible={visibility.isVisible || visibility.isBelowThreshold}
                showText={showText}
                showMask={showMask}
                // Collapse is an edit-only aid: even though the flag persists across images,
                // preview always shows the real boxes/masks.
                collapseToPoints={collapseActive}
                isEditMode={isEditMode}
                key={annotation.id}
                annotation={annotation}
                color={color}
                outlineColor={outlineColor}
                outsideBoundaryCandidateState={outsideBoundaryCandidateState ?? undefined}
                totalScale={totalScale}
                fitScale={fitScale}
                selected={selectedAnnotationId === annotation.id}
                isHovered={hoveredIndex === index}
                suppressHoverPreview={Boolean(draftBox)}
                rawWidth={rawWidth}
                rawHeight={rawHeight}
                measurementDescriptors={measurementDescriptors}
                hiddenMeasurementKeys={hiddenMeasurementKeys}
                highlightedKeypointName={selectedAnnotationId === annotation.id ? highlightedKeypointName : null}
                getVisibleImageRect={getVisibleImageRect}
                measurementEditing={measurementEditing}
                onSelect={() => selectAnnotation(annotation.id)}
                onToggleAtPoint={handleImageDoubleClick}
                onBBoxChange={(bbox, update) => updateManualBBox(annotation.id, bbox, update)}
                onMeasurementKeypointChange={(keypointName, x, y, recomputedMeters) =>
                  updateMeasurementKeypoint(annotation.id, keypointName, x, y, recomputedMeters)
                }
                onMeasurementDraft={handleMeasurementDraft}
                onInvalidMeasurement={handleInvalidMeasurement}
              />
            );
          })}

          {hoverPoint && isEditMode && displayAnnotations && !draftBox && (
            <>
              <Line
                points={[hoverPoint.x, 0, hoverPoint.x, rawHeight]}
                stroke="white"
                strokeWidth={2 / totalScale}
                dash={[8 / totalScale, 8 / totalScale]}
                listening={false}
              />
              <Line
                points={[0, hoverPoint.y, rawWidth, hoverPoint.y]}
                stroke="white"
                strokeWidth={2 / totalScale}
                dash={[8 / totalScale, 8 / totalScale]}
                listening={false}
              />
            </>
          )}

          {activeDraftBox && (
            <Rect
              x={activeDraftBox.x}
              y={activeDraftBox.y}
              width={activeDraftBox.width}
              height={activeDraftBox.height}
              stroke={classRegistry.isMultiClass ? activeClassColor : "#facc15"}
              strokeWidth={3 / totalScale}
              dash={[8 / totalScale, 8 / totalScale]}
              fill="rgba(250, 204, 21, 0.08)"
              listening={false}
            />
          )}
        </AnnotationViewerBase>

        {depthDebugDraft && (
          <div
            role="status"
            aria-live="polite"
            className={`pointer-events-none absolute z-20 min-w-44 rounded-md border border-white/20 border-l-4 bg-slate-950/90 px-4 py-3 text-white shadow-xl backdrop-blur-sm ${MEASUREMENT_HUD_CORNER_CLASSES[measurementHudCorner]}`}
            style={{ borderLeftColor: depthDebugDraft.color }}>
            {/* Naming the AXIS alone ("New Core Length") leaves the labeler guessing
                whether they are moving the base or the tip. Both are learned by name,
                so the drag says which one, and echoes the persisted name underneath. */}
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300">
              New {depthDebugDraft.label}
            </div>
            <div className="font-mono text-[11px] text-slate-400">{depthDebugDraft.keypointName}</div>
            {depthDebugDraft.valueCm != null ? (
              <div className="mt-0.5 font-mono text-3xl font-semibold leading-none tabular-nums">
                {depthDebugDraft.valueCm.toFixed(1)} <span className="text-base text-slate-300">cm</span>
              </div>
            ) : depthDebugDraft.pixelLengthPx != null ? (
              // No depth to measure against: report the only length that exists,
              // the on-screen one, so the drag still has live feedback.
              <>
                <div className="mt-0.5 font-mono text-3xl font-semibold leading-none tabular-nums">
                  {Math.round(depthDebugDraft.pixelLengthPx)} <span className="text-base text-slate-300">px</span>
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-300">
                  No depth — position only
                </div>
              </>
            ) : (
              <div className="mt-1 text-base font-semibold text-orange-300">No valid depth</div>
            )}
          </div>
        )}

        {isEditMode && displayAnnotations && hasMeasurementAnnotations && depthNotice && (
          <div
            className={`absolute left-2 top-2 z-10 flex max-w-sm items-center gap-2 rounded px-2 py-1 text-xs text-white ${DEPTH_NOTICE_TONE_CLASSES[depthNotice.tone]}`}>
            <span>{depthNotice.text}</span>
            {depthNotice.retryable && onRetryDepthLoad && (
              <button
                type="button"
                onClick={onRetryDepthLoad}
                className="shrink-0 rounded bg-white/20 px-1.5 py-0.5 font-medium hover:bg-white/30">
                Retry
              </button>
            )}
          </div>
        )}

        {showDepthDebug && depthFrame && (
          <DepthDebugView
            depthFrame={depthFrame}
            annotation={selectedDepthDebugAnnotation}
            viewport={viewport}
            liveDraft={depthDebugDraft}
            onClose={() => onCloseDepthDebug?.()}
          />
        )}
      </div>

      {displayAnnotations && (
        <AnnotationSidebar
          isEditMode={isEditMode}
          maskToggle={hasMask && onShowMaskChange ? { value: showMask, onChange: onShowMaskChange } : undefined}
          collapseToggle={
            canCollapse && onCollapseToPointsChange
              ? { value: collapseToPoints, onChange: onCollapseToPointsChange }
              : undefined
          }
          outsideBoundaryToggle={
            hasValidBoundary && onShowOutsideBoundaryAnnotationsChange
              ? {
                  value: showOutsideBoundaryAnnotations,
                  onChange: onShowOutsideBoundaryAnnotationsChange,
                }
              : undefined
          }
          classControls={classControls}
          stats={
            <div className="space-y-1">
              <p className="font-semibold">
                {formatAnnotationObjectCount(countedIndices.size, countedBeforeClassFilterCount)}
              </p>
              {boundaryAreaSquareMeters != null && boundaryAreaSquareMeters > 0 && (
                <p>
                  {(countedIndices.size / boundaryAreaSquareMeters).toFixed(1)}
                  {countedIndices.size === countedBeforeClassFilterCount ? "" : " shown"} per m²
                </p>
              )}
            </div>
          }
          instructions={
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <SquareDashedMousePointer className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Drag to add an object</span>
              </div>
              <div className="flex items-start gap-2">
                <MousePointer2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Click a box to select it</span>
              </div>
              <div className="flex items-start gap-2">
                <Trash2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Double-click or press Delete to remove</span>
              </div>
              <div className="flex items-start gap-2">
                <CornerDownRight className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Drag selected manual boxes to move</span>
              </div>
              <div className="flex items-start gap-2">
                <Expand className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Use corner handles to resize manual boxes</span>
              </div>
            </div>
          }
          measurementToggles={measurementAxisLegend.map((axis) => ({
            ...axis,
            hidden: hiddenMeasurementKeys.has(axis.key),
            // A locator points at the SELECTED box, so once there is a selection the
            // rows come from that box, and vanish for an axis it does not carry (mixed
            // images are real — a hand-drawn box can sit beside measured ones). With
            // NOTHING selected the image-wide rows still render, inert, or the feature
            // would be invisible until the reviewer happened to click a box. Collapse
            // draws a bare centre dot and no landmarks, so it drops them either way.
            // The axis row and its park checkbox always stay: those are image-wide.
            endpoints: collapseActive
              ? undefined
              : selectedAnnotationId
                ? selectedAnnotationLandmarks.get(axis.key)
                : axis.endpoints,
          }))}
          onToggleMeasurement={toggleMeasurementAxis}
          onHighlightKeypoint={setHighlightedKeypointName}
          // The highlight targets one box, so the rows go inert without a selection.
          landmarksInteractive={Boolean(selectedAnnotationId)}
          confidenceSlider={
            // Keep an ACTIVE filter's slider visible even on constant-data images (where
            // the has*Data gate is false), so a restored/persisted filter can always be
            // seen and reset — otherwise it hides objects with no visible control.
            isEditMode && displayAnnotations && (hasConfidenceData || activatedFilters.has("sensitivity"))
              ? {
                  value: confidenceThreshold,
                  inactive: !activatedFilters.has("sensitivity"),
                  onChange: onConfidenceThresholdChange,
                }
              : undefined
          }
          maxLengthSlider={
            isEditMode &&
            displayAnnotations &&
            (hasMaxLengthData || activatedFilters.has("maxLength")) &&
            maxLengthM !== null
              ? {
                  value: maxLengthM,
                  // Extend past the image's data max for a restored value that exceeds it
                  // (and floor positive), so the slider never gets value>max or max=null.
                  max: resolveFilterSliderMax(maxLengthDataM, maxLengthM),
                  inactive: !activatedFilters.has("maxLength"),
                  onChange: onMaxLengthChange,
                }
              : undefined
          }
          maxCameraDistanceSlider={
            isEditMode &&
            displayAnnotations &&
            (hasMaxCameraDistanceData || activatedFilters.has("cameraDistance")) &&
            maxCameraDistanceM !== null
              ? {
                  value: maxCameraDistanceM,
                  max: resolveFilterSliderMax(maxCameraDistanceDataM, maxCameraDistanceM),
                  inactive: !activatedFilters.has("cameraDistance"),
                  onChange: onMaxCameraDistanceChange,
                }
              : undefined
          }
          maxBboxLengthSlider={
            isEditMode &&
            displayAnnotations &&
            (hasMaxBboxLengthData || activatedFilters.has("bboxLength")) &&
            maxBboxLengthPx !== null
              ? {
                  value: maxBboxLengthPx,
                  max: resolveFilterSliderMax(maxBboxLengthDataPx, maxBboxLengthPx),
                  inactive: !activatedFilters.has("bboxLength"),
                  onChange: onMaxBboxLengthChange,
                }
              : undefined
          }
          maxOverlapIouSlider={
            isEditMode && displayAnnotations && (hasOverlapData || activatedFilters.has("maxOverlapIou"))
              ? {
                  value: maxOverlapIou,
                  inactive: !activatedFilters.has("maxOverlapIou"),
                  onChange: onMaxOverlapIouChange,
                }
              : undefined
          }
          onResetFilters={resetFilters}
        />
      )}

      <OutsideBoundaryWarningDialog
        open={pendingOutsideBoundaryAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingOutsideBoundaryAction(null);
        }}
        onContinue={() => {
          setOutsideBoundarySessionChoice("bbox", "allow");
          if (retainedPendingOutsideBoundaryAction) {
            commitOutsideBoundaryAction(retainedPendingOutsideBoundaryAction);
          }
        }}
      />
    </div>
  );
};

export const Segmentation = forwardRef(SegmentationComponent);
Segmentation.displayName = "Segmentation";
